import { esgCodes } from '@/data/esgCode';
import { useSingleIndexQuery } from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/components/components/singleIndex/hooks/useSingleIndexQuery';
import { useSelector } from 'react-redux';
import { useIndex } from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/components/components/singleIndex/hooks/useIndex';
import { useCompositeScore } from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/components/components/singleIndex/hooks/useCompositeScore';
import { useIndividualScore } from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/components/components/singleIndex/hooks/useIndividualScore';
import { useTranslation } from 'react-i18next';
import DatePicker from '@/components/ui/datepicker';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const SingleIndex = () => {
  const query = useSelector((state: any) => state.analytics.indexQuery.index.singleIndex);
  const [setQuery] = useSingleIndexQuery();
  const [getIndex, setIndex] = useIndex();
  const [compositeScore, setCompositeScore] = useCompositeScore();
  const [individualScore, setIndividualScore] = useIndividualScore();
  const [t] = useTranslation('analytics');

  const handleIndividualScore = (code: number) => {
    if (individualScore.includes(code)) {
      setIndividualScore(individualScore.filter((el) => el !== code));
    } else {
      setIndividualScore([...individualScore, code]);
    }
  };

  return (
    <div className="space-y-4 m-4">
      <div className="w-full space-y-1">
        <div className="font-medium">{t('Time window')}</div>
        <div className="flex items-center gap-2">
          <DatePicker
            className="w-full"
            value={query.from}
            onChange={(e) => setQuery({ from: e })}
          />
          <DatePicker className="w-full" value={query.to} onChange={(e) => setQuery({ to: e })} />
        </div>
      </div>
      <div className="space-y-1">
        <div className="font-medium">{t('Data type')}</div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={query.compositeScore.materiality === '1'}
            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-primary"
            onClick={() => {
              if (query.dataType === '') {
                setQuery({ dataType: '1', compositeScore: { materiality: '1' } });
              }
              if (query.dataType === '0') {
                if (query.individualScore.esgCode === '') {
                  setQuery({ dataType: '', compositeScore: { materiality: '' } });
                } else setQuery({ dataType: '1', compositeScore: { materiality: '' } });
              }
              if (query.dataType === '1') {
                setQuery({ dataType: '0,1', compositeScore: { materiality: '1' } });
              }
              if (query.dataType === '0,1') {
                setQuery({ dataType: '1', compositeScore: { materiality: '' } });
              }
            }}
          />
          SASB Materiality
        </div>
      </div>
      {/* <FormControl size="small" sx={{ width: '100%', ml: 1.5 }}>
              <InputLabel>Select a Materiality</InputLabel>
              <Select
                label="Select a ESG Category"
                value={compositeScore}
                onChange={(e) => setCompositeScore(e)}
                multiple
              >
                <MenuItem value={0}>Data-driven Materiality</MenuItem>
                <MenuItem value={1}>SASB Materiality</MenuItem>
              </Select>
            </FormControl> */}
      <div className="space-y-1">
        <div className="font-medium">{t('ESG Category')}</div>
        {/* <FormControl size="small" sx={{ width: '100%', ml: 1.5 }}>
                <InputLabel>Select a ESG Category</InputLabel>
                <Select
                  label="Select a ESG Category"
                  value={individualScore}
                  MenuProps={{ sx: { height: '300px' } }}
                  onChange={(e) => setIndividualScore(e)}
                  multiple
                >
                  {esgCodes.map((el, i) => (
                    <MenuItem value={Number(`${el.general_issue_code}`)} key={i}>
                      {el.general_issue_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full">
            <Button variant="outline" className="flex items-center justify-between w-full truncate">
              <span className="truncate">
                {individualScore.length > 0
                  ? individualScore
                      .map(
                        (el) =>
                          esgCodes.find((el2) => el2.general_issue_code === el)?.general_issue_name,
                      )
                      .join(',')
                  : t('Select a ESG Category')}
              </span>
              <ChevronDownIcon className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {esgCodes.map((el, i) => {
              return (
                <DropdownMenuItem
                  key={i}
                  onClick={() => handleIndividualScore(el.general_issue_code)}
                  className={cn(
                    'cursor-pointer',
                    individualScore.includes(el.general_issue_code) && 'bg-gray-200',
                  )}
                >
                  {el.general_issue_name}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="space-y-1">
        <div className="font-medium">{t('Index')}</div>

        <RadioGroup defaultValue="0" onValueChange={setIndex}>
          <div className="flex items-center gap-3">
            <RadioGroupItem value={'0'} id="0" />
            <Label htmlFor="0">{t('Pulse')}</Label>
          </div>
          <div className="flex items-center gap-3">
            <RadioGroupItem value={'1'} id="1" />
            <Label htmlFor="1">{t('Score')}</Label>
          </div>
          <div className="flex items-center gap-3">
            <RadioGroupItem value={'2'} id="2" />
            <Label htmlFor="2">{t('Momentum')}</Label>
          </div>
          <div className="flex items-center gap-3">
            <RadioGroupItem value={'3'} id="3" />
            <Label htmlFor="3">{t('ESG Exposure')}</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
};

export default SingleIndex;
